import { format, startOfDay, addDays, startOfHour, addHours, startOfMonth, addMonths, differenceInDays, differenceInHours } from 'date-fns';

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Configuration returned by getTimeAxisConfig for X-axis rendering.
 */
export interface TimeAxisConfig {
  /** Array of timestamp values (ms) where ticks should appear */
  ticks: number[];
  /** Formatter function for tick labels */
  formatTick: (value: number) => string;
}

/**
 * Generate range-aware X-axis tick positions and label formatter.
 *
 * Adapts to the visible date range:
 * - <= 1 day: hour ticks, time labels (e.g., "06:00")
 * - 2-7 days: daily ticks at midnight, date labels (e.g., "Nov 25")
 * - 8-60 days: sparse day ticks, date labels (e.g., "Nov 25")
 * - 61-365 days: monthly ticks, month labels (e.g., "Nov 2025")
 * - > 365 days: sparse monthly ticks, month/year labels
 */
export function getTimeAxisConfig(
  startDate: Date,
  endDate: Date,
  maxTicks: number = 8
): TimeAxisConfig {
  // Guard against reversed or zero-length ranges
  if (endDate.getTime() <= startDate.getTime()) {
    return {
      ticks: [startDate.getTime()],
      formatTick: (value: number) => format(new Date(value), 'MMM d, H:mm'),
    };
  }

  const diffHours = differenceInHours(endDate, startDate);
  const diffDays = differenceInDays(endDate, startDate);

  // --- INTRADAY: <= 24 hours ---
  if (diffHours <= 24) {
    const intervalHours = diffHours <= 6 ? 1 : diffHours <= 12 ? 2 : 4;
    const ticks = generateHourTicks(startDate, endDate, intervalHours, maxTicks);
    return {
      ticks,
      formatTick: (value: number) => format(new Date(value), 'H:mm'),
    };
  }

  // --- WEEK VIEW: 2-7 days ---
  if (diffDays <= 7) {
    const ticks = generateMidnightTicks(startDate, endDate, 1, maxTicks);
    return {
      ticks,
      formatTick: (value: number) => format(new Date(value), 'MMM d'),
    };
  }

  // --- MONTH VIEW: 8-60 days ---
  if (diffDays <= 60) {
    const targetTicks = Math.min(maxTicks, Math.max(4, Math.floor(maxTicks * 0.8)));
    const stepDays = Math.max(1, Math.ceil(diffDays / targetTicks));
    const ticks = generateMidnightTicks(startDate, endDate, stepDays, maxTicks);
    return {
      ticks,
      formatTick: (value: number) => format(new Date(value), 'MMM d'),
    };
  }

  // --- LONG RANGE: 61-365 days ---
  if (diffDays <= 365) {
    const ticks = generateMonthBoundaryTicks(startDate, endDate, 1, maxTicks);
    return {
      ticks,
      formatTick: (value: number) => format(new Date(value), 'MMM yyyy'),
    };
  }

  // --- MULTI-YEAR: > 365 days ---
  const everyN = Math.max(1, Math.ceil(diffDays / 365));
  const ticks = generateMonthBoundaryTicks(startDate, endDate, everyN, maxTicks);
  return {
    ticks,
    formatTick: (value: number) => format(new Date(value), 'MMM yyyy'),
  };
}

function generateHourTicks(start: Date, end: Date, intervalHours: number, maxTicks: number): number[] {
  const ticks: number[] = [];
  let current = startOfHour(start);
  const endMs = end.getTime();

  while (current.getTime() <= endMs) {
    ticks.push(current.getTime());
    current = addHours(current, intervalHours);
  }

  return limitTicks(ticks, maxTicks);
}

function generateMidnightTicks(start: Date, end: Date, stepDays: number, maxTicks: number): number[] {
  const ticks: number[] = [];
  let current = startOfDay(start);
  const endMs = end.getTime();

  while (current.getTime() <= endMs) {
    ticks.push(current.getTime());
    current = addDays(current, stepDays);
  }

  return limitTicks(ticks, maxTicks);
}

function generateMonthBoundaryTicks(start: Date, end: Date, everyN: number, maxTicks: number): number[] {
  const ticks: number[] = [];
  let current = startOfMonth(start);
  const endMs = end.getTime();
  let i = 0;

  while (current.getTime() <= endMs) {
    if (i % everyN === 0) {
      ticks.push(current.getTime());
    }
    current = addMonths(current, 1);
    i++;
  }

  return limitTicks(ticks, maxTicks);
}

function limitTicks(ticks: number[], maxTicks: number): number[] {
  if (ticks.length === 0) return [];
  if (maxTicks <= 1) return [ticks[0]!];
  if (ticks.length <= maxTicks) return ticks;

  const result: number[] = [ticks[0]!];
  const step = (ticks.length - 1) / (maxTicks - 1);

  for (let i = 1; i < maxTicks - 1; i++) {
    const index = Math.round(i * step);
    result.push(ticks[index]!);
  }

  result.push(ticks[ticks.length - 1]!);
  return result;
}

/**
 * Result from calculateNiceAxisTicks containing all Y-axis configuration
 */
export interface NiceAxisConfig {
  /** Array of evenly-spaced tick values */
  ticks: number[];
  /** Domain [min, max] aligned with tick values */
  domain: [number, number];
  /** Number of decimal places to display */
  decimals: number;
  /** Tick formatter function with appropriate precision */
  tickFormatter: (value: number) => string;
}

/**
 * Calculate "nice" evenly-spaced Y-axis ticks with appropriate decimal precision.
 *
 * This solves common chart axis issues:
 * - Awkward tick values (502.4, 447.6) → nice round numbers (400, 450, 500)
 * - Duplicate labels (0.1, 0.1) → appropriate decimals (0.05, 0.10, 0.15)
 * - Uneven spacing → mathematically even intervals
 *
 * @param dataMin Minimum value in data
 * @param dataMax Maximum value in data
 * @param targetTickCount Desired number of ticks (default 4-5)
 * @param includeZero If true, ensure zero is included in domain when close to data
 * @returns Configuration object with ticks, domain, decimals, and tickFormatter
 */
export function calculateNiceAxisTicks(
  dataMin: number,
  dataMax: number,
  targetTickCount: number = 5,
  includeZero: boolean = true
): NiceAxisConfig {
  // Handle edge case: all values are the same
  if (dataMin === dataMax) {
    const value = dataMin;
    const padding = value === 0 ? 1 : Math.abs(value) * 0.1;
    const min = value - padding;
    const max = value + padding;
    return calculateNiceAxisTicks(min, max, targetTickCount, includeZero);
  }

  // Calculate the raw range
  let rangeMin = dataMin;
  let rangeMax = dataMax;

  // Optionally include zero if data is close to it
  if (includeZero && rangeMin > 0 && rangeMin < rangeMax * 0.2) {
    rangeMin = 0;
  }

  const range = rangeMax - rangeMin;

  // Calculate a "nice" step size
  // We want steps like 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, etc.
  const rawStep = range / (targetTickCount - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;

  // Round to nearest "nice" number: 1, 2, 2.5, 5, or 10
  let niceStep: number;
  if (normalized <= 1) {
    niceStep = 1 * magnitude;
  } else if (normalized <= 2) {
    niceStep = 2 * magnitude;
  } else if (normalized <= 2.5) {
    niceStep = 2.5 * magnitude;
  } else if (normalized <= 5) {
    niceStep = 5 * magnitude;
  } else {
    niceStep = 10 * magnitude;
  }

  // Calculate nice domain boundaries
  const niceMin = Math.floor(rangeMin / niceStep) * niceStep;
  const niceMax = Math.ceil(rangeMax / niceStep) * niceStep;

  // Calculate decimal places needed based on step size
  let decimals = 0;
  if (niceStep < 1) {
    // Count decimal places needed
    decimals = Math.ceil(-Math.log10(niceStep));
    // Ensure we have at least enough decimals to distinguish ticks
    const stepDecimals = niceStep.toString().split('.')[1]?.length || 0;
    decimals = Math.max(decimals, stepDecimals);
  }

  // Generate tick values using index-based calculation to avoid floating point accumulation errors
  const ticks: number[] = [];
  const tickCount = Math.round((niceMax - niceMin) / niceStep) + 1;
  for (let i = 0; i < tickCount; i++) {
    const tick = niceMin + i * niceStep;
    // Round to the calculated precision to clean up floating point errors
    ticks.push(Number(tick.toFixed(decimals + 2)));
  }

  // Create formatter with the calculated precision
  const tickFormatter = (value: number): string => {
    return value.toFixed(decimals);
  };

  return {
    ticks,
    domain: [niceMin, niceMax],
    decimals,
    tickFormatter,
  };
}

/**
 * Options for target-range-aware Y-axis calculation.
 *
 * Both dataMin/dataMax and targetMin/targetMax are optional:
 * - Data + target -> target range +/-10%, extended by data +/-10% if data exceeds
 * - Data only -> pure data-based scaling
 * - Target only -> target range +/-10% (no-data fallback for empty charts)
 * - Neither -> returns null (caller should show "no data" state)
 */
export interface TargetRangeAxisOptions {
  /** Minimum value in data (optional - omit when chart has no data) */
  dataMin?: number;
  /** Maximum value in data (optional - omit when chart has no data) */
  dataMax?: number;
  /** Target range minimum (optional - from user config or global defaults) */
  targetMin?: number;
  /** Target range maximum (optional - from user config or global defaults) */
  targetMax?: number;
  /** Number of ticks (default 5) */
  targetTickCount?: number;
}

/**
 * Calculate Y-axis bounds using target ranges with +/-10% padding.
 *
 * Algorithm:
 * 1. Start with the target range as the baseline visible range
 * 2. Add 10% padding of the target range span to both ends
 * 3. If data values fall outside this padded range, extend to data min/max +/-10% of the data span
 * 4. Pass the resulting min/max to calculateNiceAxisTicks for clean tick values
 *
 * Priority:
 * - Data + target range -> smart merged scaling
 * - Data only -> pure data-based scaling (no target configured)
 * - Target only -> target +/-10% (empty chart with configured range)
 * - Neither -> returns null (chart should show "no data" message)
 */
export function calculateTargetRangeAxis(options: TargetRangeAxisOptions): NiceAxisConfig | null {
  const { dataMin, dataMax, targetMin, targetMax, targetTickCount = 5 } = options;

  const hasData = dataMin != null && dataMax != null;
  const hasTarget = targetMin != null && targetMax != null && targetMin < targetMax;

  // Neither data nor valid target range -> caller should show empty state
  if (!hasData && !hasTarget) {
    return null;
  }

  // Target range only (no data) -> show target range +/-10%
  if (!hasData && hasTarget) {
    const targetSpan = targetMax! - targetMin!;
    const padding = targetSpan * 0.1;
    return calculateNiceAxisTicks(targetMin! - padding, targetMax! + padding, targetTickCount, false);
  }

  // Data only (no valid target range) -> pure data-based scaling
  if (hasData && !hasTarget) {
    return calculateNiceAxisTicks(dataMin!, dataMax!, targetTickCount, false);
  }

  // Both data and target range -> smart merged scaling
  const targetSpan = targetMax! - targetMin!;
  const targetPadding = targetSpan * 0.1;

  // Start with target range +/-10%
  let axisMin = targetMin! - targetPadding;
  let axisMax = targetMax! + targetPadding;

  // Extend if data falls outside the padded target range
  // Use consistent 10% of TARGET span for padding (not data span)
  if (dataMin! < axisMin) {
    axisMin = dataMin! - targetPadding;
  }
  if (dataMax! > axisMax) {
    axisMax = dataMax! + targetPadding;
  }

  return calculateNiceAxisTicks(axisMin, axisMax, targetTickCount, false);
}
